import { reportNumber, runReport, type ReportResult } from './reports';

export interface WebsiteReportDay {
  date: string;
  label: string;
  visits: number;
  users: number;
  conversions: number;
  previousVisits: number;
  previousUsers: number;
  previousDate: string;
}

export interface WebsiteReportType {
  label: string;
  count: number;
}

export interface WebsiteReportChart {
  visits: number;
  users: number;
  conversions: number;
  conversionRate: string | null;
  types: WebsiteReportType[];
  days: WebsiteReportDay[];
  /** The last day is still in progress, so the chart draws it dotted. */
  openDay: boolean;
}

export interface WebsiteReportMonth {
  monthKey: string;
  label: string;
  since: string;
  until: string;
  visits: number;
  unique: number;
  conversionRate?: number;
  conversionTypes: WebsiteReportType[];
  isCurrentMonth: boolean;
  openDay: boolean;
}

export interface WebsiteReportYear {
  yearLabel: string;
  visits: number;
  unique: number;
  conversionRate?: number;
  conversionTypes: WebsiteReportType[];
  months: WebsiteReportMonth[];
}

export function todayIn(timeZone: string, now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function shiftDate(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function eachDate(since: string, until: string): string[] {
  const dates: string[] = [];
  let cursor = since;
  while (cursor <= until) {
    dates.push(cursor);
    cursor = shiftDate(cursor, 1);
  }
  return dates;
}

function dayLabel(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function monthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function lastDayOfMonth(year: number, month: number): string {
  const day = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function byDay(result: ReportResult, metric: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of result.rows) {
    const day = String(row.day ?? '');
    if (!day) continue;
    const raw = row[metric];
    out[day] = typeof raw === 'number' ? raw : Number(raw ?? 0);
  }
  return out;
}

function typeRows(result: ReportResult): WebsiteReportType[] {
  return result.rows.map((row) => ({
    label: String(row.conversion_type ?? ''),
    count: typeof row.conversions === 'number' ? row.conversions : Number(row.conversions ?? 0),
  }));
}

function rate(visits: number, conversions: number, configured: boolean): string | null {
  if (!configured) return null;
  return (visits > 0 ? (conversions / visits) * 100 : 0).toFixed(1);
}

/** One website window, with the previous period aligned by day. */
export async function fetchWebsiteChart(
  businessId: string,
  since: string,
  until: string,
  timeZone: string,
): Promise<WebsiteReportChart> {
  const currentDates = eachDate(since, until);
  const prevUntil = shiftDate(since, -1);
  const prevSince = shiftDate(prevUntil, -(Math.max(currentDates.length, 1) - 1));
  const previousDates = eachDate(prevSince, prevUntil);
  const currentWindow = `SINCE ${since} UNTIL ${until}`;
  const previousWindow = `SINCE ${prevSince} UNTIL ${prevUntil}`;
  const [totals, types, currentDays, previousDays] = await Promise.all([
    runReport(businessId, `FROM website SHOW visits, users, conversions ${currentWindow}`),
    runReport(businessId, `FROM website SHOW conversions GROUP BY conversion_type ${currentWindow}`),
    runReport(businessId, `FROM website SHOW visits, users, conversions GROUP BY day ${currentWindow}`),
    runReport(businessId, `FROM website SHOW visits, users GROUP BY day ${previousWindow}`),
  ]);
  const visits = reportNumber(totals, 'visits');
  const conversions = reportNumber(totals, 'conversions');
  const currentVisits = byDay(currentDays, 'visits');
  const currentUsers = byDay(currentDays, 'users');
  const currentConversions = byDay(currentDays, 'conversions');
  const previousVisits = byDay(previousDays, 'visits');
  const previousUsers = byDay(previousDays, 'users');
  const rows = typeRows(types);
  return {
    visits,
    users: reportNumber(totals, 'users'),
    conversions,
    conversionRate: rate(visits, conversions, rows.length > 0),
    types: rows,
    openDay: until === todayIn(timeZone),
    days: currentDates.map((date, index) => ({
      date,
      label: dayLabel(date),
      visits: currentVisits[date] || 0,
      users: currentUsers[date] || 0,
      conversions: currentConversions[date] || 0,
      previousVisits: previousVisits[previousDates[index]] || 0,
      previousUsers: previousUsers[previousDates[index]] || 0,
      previousDate: previousDates[index] || '',
    })),
  };
}

/** January through the current month, each from the website report. */
export async function fetchWebsiteYear(businessId: string, timeZone: string): Promise<WebsiteReportYear> {
  const today = todayIn(timeZone);
  const year = Number(today.slice(0, 4));
  const currentMonth = Number(today.slice(5, 7));
  const yearSince = `${year}-01-01`;
  const windows = Array.from({ length: currentMonth }, (_, index) => {
    const month = index + 1;
    const since = `${year}-${String(month).padStart(2, '0')}-01`;
    const monthEnd = lastDayOfMonth(year, month);
    const until = monthEnd > today ? today : monthEnd;
    return { monthKey: since.slice(0, 7), since, until };
  });

  const [yearTotals, yearTypes, ...monthResults] = await Promise.all([
    runReport(businessId, `FROM website SHOW visits, users, conversions SINCE ${yearSince} UNTIL ${today}`),
    runReport(businessId, `FROM website SHOW conversions GROUP BY conversion_type SINCE ${yearSince} UNTIL ${today}`),
    ...windows.flatMap((window) => [
      runReport(
        businessId,
        `FROM website SHOW visits, users, conversions SINCE ${window.since} UNTIL ${window.until}`,
      ),
      runReport(
        businessId,
        `FROM website SHOW conversions GROUP BY conversion_type SINCE ${window.since} UNTIL ${window.until}`,
      ),
    ]),
  ]);

  const yearTypeList = typeRows(yearTypes);
  const yearVisits = reportNumber(yearTotals, 'visits');
  const yearConversions = reportNumber(yearTotals, 'conversions');
  return {
    yearLabel: String(year),
    visits: yearVisits,
    unique: reportNumber(yearTotals, 'users'),
    conversionRate: yearTypeList.length > 0
      ? Number(rate(yearVisits, yearConversions, true))
      : undefined,
    conversionTypes: yearTypeList,
    months: windows.map((window, index) => {
      const totals = monthResults[index * 2];
      const types = typeRows(monthResults[index * 2 + 1]);
      const visits = reportNumber(totals, 'visits');
      const conversions = reportNumber(totals, 'conversions');
      return {
        monthKey: window.monthKey,
        label: monthLabel(window.monthKey),
        since: window.since,
        until: window.until,
        visits,
        unique: reportNumber(totals, 'users'),
        conversionRate: types.length > 0 ? Number(rate(visits, conversions, true)) : undefined,
        conversionTypes: types,
        isCurrentMonth: window.until === today,
        openDay: window.until === today,
      };
    }),
  };
}
